import axios from 'axios';
import type {
  DietOrdersResponse,
  DietOrderDetail,
  DietTemplate,
  CalendarSlot,
} from '../types/dietitian.types';

// Базовий URL — той самий що й cabinet
const getBaseUrl = () => {
  if (typeof window !== 'undefined' && (window as any).dietitianVars?.wpApiUrl) {
    return (window as any).dietitianVars.wpApiUrl;
  }
  if (typeof window !== 'undefined' && (window as any).cabinetVars?.wpApiUrl) {
    return (window as any).cabinetVars.wpApiUrl;
  }
  return '/wp-json/api_front/v1';
};

const getToken = (): string => {
  return (window as any).dietitianVars?.token
    || sessionStorage.getItem('diet_token')
    || '';
};

const getNonce = (): string => {
  return (window as any).dietitianVars?.nonce || (window as any).cabinetVars?.nonce || '';
};

export const dietClient = axios.create({
  baseURL: getBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

dietClient.interceptors.request.use((config) => {
  const token = getToken();
  const nonce = getNonce();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (nonce) config.headers['X-WP-Nonce'] = nonce;
  if (config.method === 'get') {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  }
  return config;
});

// ── Orders ──────────────────────────────────────────────────

export const getDietOrders = async (tab = 'all', search = ''): Promise<DietOrdersResponse> => {
  const res = await dietClient.get('/diet/orders', { params: { tab, search } });
  return res.data;
};

export const getDietOrder = async (id: number): Promise<DietOrderDetail> => {
  const res = await dietClient.get(`/diet/orders/${id}`);
  return res.data;
};

export const uploadDietPdf = async (orderId: number, file: File): Promise<{ pdf_url: string; medyk_sync: boolean }> => {
  const form = new FormData();
  form.append('pdf', file);
  const res = await dietClient.post(`/diet/orders/${orderId}/pdf`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const saveDietZalecenia = async (
  orderId: number,
  zalecenia: string,
  draft = false
): Promise<{ success: boolean }> => {
  const res = await dietClient.post(`/diet/orders/${orderId}/zalecenia`, { zalecenia, draft });
  return res.data;
};

export const sendDietToPatient = async (orderId: number): Promise<{ success: boolean; status: string }> => {
  const res = await dietClient.post(`/diet/orders/${orderId}/send`);
  return res.data;
};

// [НОВИЙ] Зберегти нотатку адміна про пацієнта (тільки адмін)
export const saveAdminNote = async (orderId: number, note: string): Promise<{ success: boolean }> => {
  const res = await dietClient.post(`/diet/orders/${orderId}/admin-note`, { note });
  return res.data;
};

// [НОВИЙ] Cancel order directly from order card
export const cancelOrderKeepSlotByOrderId = async (orderId: number): Promise<{ success: boolean }> => {
  const res = await dietClient.post(`/diet/orders/${orderId}/cancel-keep-slot`);
  return res.data;
};

// [НОВИЙ] Restore cancelled order
export const restoreOrder = async (orderId: number): Promise<{ success: boolean }> => {
  const res = await dietClient.post(`/diet/orders/${orderId}/restore`);
  return res.data;
};

// [НОВИЙ] Отримати всі замовлення для адмін-панелі
export const getDietOrdersForAdmin = async (tab = 'all', search = '') => {
  const res = await dietClient.get('/diet/orders', { params: { tab, search } });
  return res.data;
};

// ── Templates ───────────────────────────────────────────────

export const getDietTemplates = async (): Promise<{ templates: DietTemplate[] }> => {
  const res = await dietClient.get('/diet/templates');
  return res.data;
};

export const saveDietTemplate = async (tpl: Omit<DietTemplate, 'id'> & { id?: string }): Promise<{ template: DietTemplate }> => {
  const res = await dietClient.post('/diet/templates', tpl);
  return res.data;
};

export const deleteDietTemplate = async (id: string): Promise<void> => {
  await dietClient.delete(`/diet/templates/${id}`);
};

// ── Calendar ─────────────────────────────────────────────────

export const getCalendarSlots = async (daysAhead = 14): Promise<{ slots: CalendarSlot[]; configured: boolean }> => {
  const res = await dietClient.get('/diet/calendar/slots', { params: { days_ahead: daysAhead } });
  return res.data;
};

export const getCalendarEvents = async (): Promise<{ events: any[] }> => {
  const res = await dietClient.get('/diet/calendar/events');
  return res.data;
};

export const generateCalendarSlots = async (data: { date: string, start_time: string, end_time: string, duration?: number }): Promise<{ success: boolean, count: number }> => {
  const res = await dietClient.post('/diet/calendar/generate-slots', data);
  return res.data;
};

export const deleteCalendarEvent = async (id: string): Promise<{ success: boolean }> => {
  const res = await dietClient.delete(`/diet/calendar/events/${id}`);
  return res.data;
};

export const assignPatientToEvent = async (id: string, patientName: string): Promise<{ success: boolean }> => {
  const res = await dietClient.post(`/diet/calendar/events/${id}/assign`, { patientName });
  return res.data;
};

export const cancelEventKeepSlot = async (id: string): Promise<{ success: boolean }> => {
  const res = await dietClient.post(`/diet/calendar/events/${id}/cancel-keep-slot`);
  return res.data;
};

export const downloadDietOrderPdf = async (
  orderId: number,
  filename?: string,
  openInNewTab: boolean = false
): Promise<void> => {
  let newTab: Window | null = null;
  if (openInNewTab) {
    newTab = window.open('', '_blank');
  }

  try {
    const response = await dietClient.get(`/diet/download-pdf/${orderId}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    if (openInNewTab && newTab) {
      newTab.location.href = blobUrl;
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } else {
      if (newTab) newTab.close();

      let downloadFilename = filename;
      if (!downloadFilename) {
        const disposition = response.headers?.['content-disposition'];
        if (disposition && disposition.includes('filename=')) {
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            downloadFilename = match[1].replace(/['"]/g, '');
          }
        }
      }
      if (!downloadFilename) {
        downloadFilename = `jadlospis_${orderId}.pdf`;
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    }
  } catch (err: any) {
    if (newTab) newTab.close();
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        if (json.error || json.message) {
          err.message = json.error || json.message;
        }
      } catch {
        // ignore
      }
    }
    console.error('Failed to download PDF:', err);
    throw err;
  }
};
